import type {
  Address,
  JobServiceOption,
  ObjectIdOrString,
  Season,
  SimpleLatLng,
} from "@mysnowscape/snow-lib";
import type { colors } from "../components/Maps/ActiveRoutes/colors";

export type RouteType = "recurring" | "temporary";
export type RecencyFilter = "2h" | "6h" | "24h" | "1m" | "6m" | "1y" | null;
export type ServiceTimeSource = "historical" | "siteMap" | "manual" | "default";
export type LockPosition = "start" | "end" | null;

/** Editor-compatible start/end payload (saved depot or one-off Places pick). */
export type RouteEndpointLocation = {
  _id?: ObjectIdOrString | null;
  companyId?: string;
  name?: string | null;
  address?: string | null;
  location: SimpleLatLng | null;
};

export const EMPTY_ROUTE_ENDPOINT: RouteEndpointLocation = {
  _id: null,
  name: null,
  address: null,
  location: null,
};

export type RouteBuilderProperty = {
  jobId: string;
  companyId: string;
  name: string;
  address: Address;
  location: SimpleLatLng;
  /** Legacy property classification. */
  propertyType: string;
  /** Company service option key (from GET company/:id/services). */
  serviceType: string;
  /** Every service ticked for this season. `serviceType` is the first. Older drafts lack it. */
  serviceTypes?: string[];
  zoneId: string;
  zoneName: string;
  season: Season;
  notes?: string;
  /** Loaded crew instructions, used to save only edited notes. */
  originalNotes?: string;
  lastServicedAt?: string;
  serviceByDeadline?: string;
  dayOfWeek?: number[];
  estimatedServiceTimeSeconds: number;
  serviceTimeSource: ServiceTimeSource;
  lock: LockPosition;
  siteMapAreaSqFt?: number;
  historicalSampleCount?: number;
  /** When someone last saved the property. Older properties may not have a save timestamp. */
  updatedAt?: string;
  /** Saved company routes from the jobs API (same shape as the route editor). */
  assignedRoutes?: Array<{
    id: string;
    name: string;
    color?: string;
  }>;
};

export type RouteGenerationInputs = {
  routeType: RouteType;
  season: Season;
  propertyFilters: {
    /** Company service option keys (from GET company/:id/services). */
    serviceTypes?: string[];
    recencyOfService?: RecencyFilter;
  };
  routeCount: number;
  /** False ignores duration limits and all service estimates. Legacy drafts default to true. */
  incorporateTime?: boolean;
  maxRouteDurationMinutes: number;
  /** Service time used when a property has no historical estimate. */
  defaultServiceTimeMinutes?: number;
  /** Properties unchecked in setup; new matches remain selected by default. */
  excludedPropertyIds?: string[];
  plannedDispatchTime: string;
  expiresAt?: string;
  startLocation: RouteEndpointLocation;
  endLocation: RouteEndpointLocation;
  sameStartAndEndLocation?: boolean;
};

/** Legacy draft data, preserved when loading and saving older drafts. */
export type RouteGenerationBaseline = {
  inputs: RouteGenerationInputs;
  properties: RouteBuilderProperty[];
  routeNamePrefix: string;
  /** Older drafts can only restore their saved contents, not original setup. */
  usesDraftSetup?: boolean;
};

export type RouteStop = {
  /** Frontend-only identity for one scheduled visit to a property. */
  propertyVisitId: string;
  propertyId: string;
  order: number;
  estimatedArrival?: string;
  estimatedDeparture?: string;
  lock: LockPosition;
};

/** Automatically includes every named color defined by the shared palette. */
export type RouteColorName = keyof typeof colors;

export type ProposedRoute = {
  id: string;
  name: string;
  color: RouteColorName;
  startLocation: RouteEndpointLocation;
  endLocation: RouteEndpointLocation;
  stops: RouteStop[];
  instructions?: string;
  metrics: {
    drivingDurationSeconds: number;
    serviceDurationSeconds: number;
    totalDurationSeconds: number;
    drivingDistanceMeters: number;
    drivingPath: string;
  };
};

export type RouteBuilderSessionStatus = "setup" | "generated" | "adjusted";
export type RouteBuilderSessionOrigin = "generated" | "blank" | "edit";
export type BuilderMode = "generate" | "edit";

/** Backend-only fields that must survive a Route Builder edit session. */
export type RoutePersistenceMetadata = {
  /** Server ID for a route added during this session; UI IDs stay stable. */
  _id?: string;
  tags: string[];
  isActive: boolean;
  expiresAt?: string | null;
};

export type UnroutedJob = {
  /** Frontend-only identity for one unassigned property visit. */
  propertyVisitId: string;
  jobId: string;
  name: string;
};

export type RouteBuilderSession = {
  id: string;
  companyId: string;
  status: RouteBuilderSessionStatus;
  /** Frontend workflow that created this session. */
  origin?: RouteBuilderSessionOrigin;
  generationBaseline?: RouteGenerationBaseline;
  inputs: RouteGenerationInputs;
  availableProperties: RouteBuilderProperty[];
  routes: ProposedRoute[];
  /** Saved holding list: generation leftovers and user additions, in manual order. */
  unroutedJobs: UnroutedJob[];
  createdAt: string;
  expiresAt?: string;
  builderMode?: BuilderMode;
  routePersistence?: Record<string, RoutePersistenceMetadata>;
};

/** One saved draft = full route builder session (all proposed routes). */
export type RouteBuilderDraft = {
  id: string;
  name: string;
  savedAt: string;
  origin?: RouteBuilderSessionOrigin;
  generationBaseline?: RouteGenerationBaseline;
  inputs: RouteGenerationInputs;
  routes: ProposedRoute[];
  availableProperties: RouteBuilderProperty[];
  unroutedJobs?: UnroutedJob[];
  builderMode?: BuilderMode;
  routePersistence?: Record<string, RoutePersistenceMetadata>;
};

/** Company service chip from GET /v2/company/:companyId/services */
export type CompanyServiceOption = Pick<JobServiceOption, "key" | "label">;

export type CompanyServicesBySeason = Record<Season, CompanyServiceOption[]>;
